import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';

declare const google: any;

/**
 * Login con Google usando Google Identity Services (script en index.html).
 * Devuelve el idToken (credential) que el API valida en el grant `givi-google`.
 *
 * GIS solo entrega el idToken desde su propio botón o desde One Tap: no se puede
 * disparar desde un botón propio. Por eso se renderiza el botón de Google y se
 * superpone transparente sobre el botón con el diseño de Givi.
 */
@Injectable({ providedIn: 'root' })
export class GoogleAuthService {

    private initialized = false;

    // Se reasigna en cada renderButton para que el credential llegue al último host.
    private onCredential: (idToken: string) => void = null;

    /**
     * Renderiza el botón de Google dentro del contenedor y avisa con el idToken
     * cada vez que el usuario completa el login.
     */
    renderButton(container: HTMLElement, onCredential: (idToken: string) => void): Promise<void> {

        this.onCredential = onCredential;

        return this.init().then(() => {

            // El ancho de GIS es en px y no acepta 100%.
            const width = Math.round(container.getBoundingClientRect().width) || 320;

            container.innerHTML = '';

            google.accounts.id.renderButton(container, {
                type: 'standard',
                theme: 'outline',
                size: 'large',
                text: 'signup_with',
                shape: 'pill',
                logo_alignment: 'center',
                width,
            });
        });
    }

    /** Olvida la cuenta elegida para que el siguiente intento vuelva a preguntar. */
    disableAutoSelect() {

        if (this.initialized) google.accounts.id.disableAutoSelect();
    }

    private init(): Promise<void> {

        if (this.initialized) return Promise.resolve();

        return this.waitForScript().then(() => {

            google.accounts.id.initialize({
                client_id: environment.googleClientId,
                callback: (response: any) => {

                    if (response?.credential && this.onCredential) {

                        this.onCredential(response.credential);
                    }
                },
                cancel_on_tap_outside: false,
                auto_select: false,
            });

            this.initialized = true;
        });
    }

    /** El script se carga async: se espera a que exista antes de inicializar. */
    private waitForScript(): Promise<void> {

        return new Promise<void>((resolve, reject) => {

            if (this.isReady()) return resolve();

            let elapsed = 0;
            const step = 100;

            const timer = setInterval(() => {

                if (this.isReady()) {

                    clearInterval(timer);
                    resolve();

                } else if ((elapsed += step) >= 10000) {

                    clearInterval(timer);
                    reject(new Error('El servicio de Google no está disponible.'));
                }
            }, step);
        });
    }

    private isReady(): boolean {

        return typeof google !== 'undefined' && google?.accounts?.id != null;
    }
}
